/**
 * Gelen banka hareketi ↔ havale işlemi için otomatik eşleştirme.
 * Tek işlem (≥80 skor, çift yönlü benzersiz) ve çoklu işlem (toplam tutar + isim) desteklenir.
 */

import {
  INVOICE_MATCH_THRESHOLDS,
  INVOICE_PERSISTED_MATCH_STATUS
} from './invoiceSchema';
import {
  evaluateKrediKartiCandidate,
  evaluateKrediKartiGroup,
  groupKrediKartiIslemler,
  isKrediKartiMatch,
  mergeKrediKartiRule
} from './invoiceKrediKartiRule';
import {
  createInvoiceMatch,
  createKrediKartiInvoiceDraft,
  createGroupInvoiceMatch,
  createMultiInvoiceMatch,
  createMultiMovementInvoiceMatch,
  fetchKrediKartiIslemler,
  findDuplicateInvoiceMatch,
  getBankingIdForAppSube,
  getExistingInvoiceMatches,
  getMatchBankMovementIds,
  isActiveInvoiceMatch,
  loadInvoiceSettingsOnce
} from './invoiceFirestoreService';
import {
  evaluateInvoiceNameMatch,
  filterIncomingBankMovements,
  filterInvoiceEligibleIslemler,
  isInvoiceAmountMatch,
  resolveIslemListDate,
  resolveIslemMusteriName,
  resolveMovementListDate,
  resolveMovementSenderName,
  suggestIslemlerForMovement,
  suggestMovementsForIslem,
  suggestMultiMatchForMovement,
  suggestMultiMovementMatchForIslem
} from '../components/invoices/invoiceMatchUtils';

/**
 * İki aday BİRBİRİNİN YERİNE GEÇEBİLİR Mİ.
 *
 * Aynı müşteri aynı gün aynı tutarda iki havale gönderip iki ayrı satış
 * kaydedildiğinde (22.10.2026 Kavaklı, BEYDA NİSA ÇALIŞKAN 2×6.625 TL) her
 * hareket iki işleme de aynı puanla uyuyor. Belirsizlik koruması ikisini birden
 * eliyor, kayıtlar "Faturalandırılacak"ta takılı kalıyordu.
 *
 * Oysa adaylar her alanıyla aynıysa hangisinin hangisine bağlandığı FARK
 * ETMEZ: taslak içeriği (müşteri, tutar, gün, ürün) ikisinde de aynı çıkar.
 * Bu yüzden yalnızca "her yönüyle eşdeğer" adaylarda beraberliğe izin verilir;
 * farklı müşteri/gün/tutar hâlâ belirsizlik sayılıp elenir.
 */
const ayniGunMu = (a, b) => {
  if (!a || !b) return false;
  const gun = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  return gun(a) === gun(b);
};

const isimleriAyniMi = (a, b) => Boolean(evaluateInvoiceNameMatch(a, b)?.nameMatch);

const esdegerIslemler = (a, b) =>
  Boolean(a && b) &&
  isInvoiceAmountMatch(Number(a.havaleEft) || 0, Number(b.havaleEft) || 0) &&
  ayniGunMu(resolveIslemListDate(a), resolveIslemListDate(b)) &&
  isimleriAyniMi(resolveIslemMusteriName(a), resolveIslemMusteriName(b));

const esdegerHareketler = (a, b) =>
  Boolean(a && b) &&
  isInvoiceAmountMatch(Number(a.amount) || 0, Number(b.amount) || 0) &&
  ayniGunMu(resolveMovementListDate(a), resolveMovementListDate(b)) &&
  isimleriAyniMi(resolveMovementSenderName(a), resolveMovementSenderName(b));

/** Beraberlikte kaç eşdeğer aday taranır. */
const ESDEGER_ADAY_TAVANI = 6;

const getLockedIds = (existingMatches = []) => {
  const lockedBankIds = new Set();
  const lockedIslemIds = new Set();
  (existingMatches || []).forEach((row) => {
    if (!isActiveInvoiceMatch(row)) return;
    if (row.matchType === 'standalone') return;
    // Çok-hareket eşleşmelerinde hepsi kilitlenmeli; yoksa aynı hareket ikinci kez faturalanır.
    getMatchBankMovementIds(row).forEach((id) => {
      if (id && id !== 'standalone') lockedBankIds.add(String(id));
    });
    const ids = Array.isArray(row.islemIds) && row.islemIds.length
      ? row.islemIds
      : row.islemId
        ? [row.islemId]
        : [];
    ids.forEach((id) => {
      if (id && id !== 'standalone') lockedIslemIds.add(String(id));
    });
  });
  return { lockedBankIds, lockedIslemIds };
};

/**
 * Otomatik yazılmaya uygun benzersiz tek-işlem eşleşme çiftlerini bulur (Firestore yazmaz).
 */
export function findAutoMatchPairs(movements, islemler, existingMatches = [], usedBankIds = new Set(), usedIslemIds = new Set()) {
  const { lockedBankIds, lockedIslemIds } = getLockedIds(existingMatches);
  const usedB = new Set([...lockedBankIds, ...usedBankIds]);
  const usedI = new Set([...lockedIslemIds, ...usedIslemIds]);

  const freeMovements = filterIncomingBankMovements(movements).filter(
    (m) => m?.id && !usedB.has(String(m.id))
  );
  const freeIslemler = filterInvoiceEligibleIslemler(islemler).filter(
    (i) => i?.id && !usedI.has(String(i.id))
  );

  if (!freeMovements.length || !freeIslemler.length) return [];

  const minScore = INVOICE_MATCH_THRESHOLDS.matched;
  const candidates = [];

  freeMovements.forEach((movement) => {
    const suggestions = suggestIslemlerForMovement(movement, freeIslemler, {
      minScore,
      limit: ESDEGER_ADAY_TAVANI
    });
    if (!suggestions.length) return;
    const best = suggestions[0];

    const beraberler = suggestions.filter(
      (s) => s.score === best.score && String(s.islem?.id) !== String(best.islem?.id)
    );
    // Beraberlik yalnızca adaylar EŞDEĞERSE kabul edilir; farklı müşteri/gün/
    // tutar hâlâ belirsizlik sayılır ve kayıt personele bırakılır.
    if (beraberler.some((s) => !esdegerIslemler(best.islem, s.islem))) return;

    const reverse = suggestMovementsForIslem(best.islem, freeMovements, {
      minScore,
      limit: 1
    })[0];
    if (!reverse) return;
    if (reverse.movement?.id !== movement.id && !esdegerHareketler(reverse.movement, movement)) return;

    candidates.push({
      movement,
      islem: best.islem,
      // Eşdeğer adaylar: en iyisi başkasına kapılırsa sıradaki kullanılır.
      esdegerIslemlerListesi: [best.islem, ...beraberler.map((s) => s.islem)],
      score: best.score,
      status: best.status,
      breakdown: best.breakdown
    });
  });

  candidates.sort((a, b) => b.score - a.score || String(a.movement.id).localeCompare(String(b.movement.id)));

  const usedBank = new Set();
  const usedIslem = new Set();
  const pairs = [];

  candidates.forEach((pair) => {
    const bankId = String(pair.movement.id);
    if (usedBank.has(bankId)) return;
    const adaylar = pair.esdegerIslemlerListesi?.length ? pair.esdegerIslemlerListesi : [pair.islem];
    const islem = adaylar.find(
      (i) =>
        i?.id &&
        !usedIslem.has(String(i.id)) &&
        !findDuplicateInvoiceMatch(existingMatches, bankId, String(i.id))
    );
    if (!islem) return;
    usedBank.add(bankId);
    usedIslem.add(String(islem.id));
    pairs.push({ ...pair, islem });
  });

  return pairs;
}

/**
 * GRUP EŞLEŞMESİ — çok hareket ↔ çok işlem.
 *
 * Müşteri bedeli birkaç havaleyle gönderip mağazada birkaç satış kaydı
 * oluştuğunda hiçbir kombinasyon tek tek tutmuyor; kayıtlar
 * "Faturalandırılacak"ta asılı kalıyordu (30.10.2026 Kavaklı — MUHAMMED ÜSAME
 * FETTAH: 3 havale 612.250 TL, 4 satış 612.250 TL).
 *
 * ÖLÇÜT KATI: aynı müşteri + aynı gün + bekleyen hareketlerin toplamı bekleyen
 * işlemlerin toplamına KURUŞU KURUŞUNA eşit. Eşit değilse hiç kurulmaz,
 * personele bırakılır.
 *
 * BAŞLANGIÇ TARİHİ: kullanıcı kararı (30.10.2026) — eski kayıtların faturaları
 * zaten kesildi, geriye dönük gruplama yapılmaz.
 */
export const GRUP_ESLESME_BASLANGIC = '2026-10-28';

/** Tek grupta taranacak en fazla kayıt (kazara devasa gruplar kurulmasın). */
const GRUP_MAX_HAREKET = 8;
const GRUP_MAX_ISLEM = 12;

const gunAnahtari = (d) => (d ? `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}` : '');

const gunBaslangictanSonraMi = (d) => {
  if (!d) return false;
  const [y, a, g] = GRUP_ESLESME_BASLANGIC.split('-').map(Number);
  return d.getTime() >= new Date(y, a - 1, g).getTime();
};

export function findAutoGroupPairs(
  movements,
  islemler,
  existingMatches = [],
  usedBankIds = new Set(),
  usedIslemIds = new Set()
) {
  const { lockedBankIds, lockedIslemIds } = getLockedIds(existingMatches);
  const usedB = new Set([...lockedBankIds, ...usedBankIds]);
  const usedI = new Set([...lockedIslemIds, ...usedIslemIds]);

  const freeMovements = filterIncomingBankMovements(movements).filter(
    (m) => m?.id && !usedB.has(String(m.id)) && gunBaslangictanSonraMi(resolveMovementListDate(m))
  );
  const freeIslemler = filterInvoiceEligibleIslemler(islemler).filter(
    (i) => i?.id && !usedI.has(String(i.id)) && gunBaslangictanSonraMi(resolveIslemListDate(i))
  );
  if (freeMovements.length < 1 || freeIslemler.length < 1) return [];

  const pairs = [];
  const kullanilanB = new Set();
  const kullanilanI = new Set();

  freeMovements.forEach((movement) => {
    if (kullanilanB.has(String(movement.id))) return;
    const gun = gunAnahtari(resolveMovementListDate(movement));
    if (!gun) return;
    const isim = resolveMovementSenderName(movement);
    if (!isim) return;

    const hareketler = freeMovements.filter(
      (m) =>
        !kullanilanB.has(String(m.id)) &&
        gunAnahtari(resolveMovementListDate(m)) === gun &&
        isimleriAyniMi(resolveMovementSenderName(m), isim)
    );
    const grupIslemler = freeIslemler.filter(
      (i) =>
        !kullanilanI.has(String(i.id)) &&
        gunAnahtari(resolveIslemListDate(i)) === gun &&
        isimleriAyniMi(resolveIslemMusteriName(i), isim)
    );

    if (!hareketler.length || !grupIslemler.length) return;
    // Tek-tek eşleşmeler kendi turlarında kurulur; grup en az bir tarafta
    // birden çok kayıt ister.
    if (hareketler.length < 2 && grupIslemler.length < 2) return;
    if (hareketler.length > GRUP_MAX_HAREKET || grupIslemler.length > GRUP_MAX_ISLEM) return;

    const hareketToplam = hareketler.reduce((t, m) => t + (Number(m.amount) || 0), 0);
    const islemToplam = grupIslemler.reduce((t, i) => t + (Number(i.havaleEft) || 0), 0);
    if (!isInvoiceAmountMatch(hareketToplam, islemToplam)) return;

    hareketler.forEach((m) => kullanilanB.add(String(m.id)));
    grupIslemler.forEach((i) => kullanilanI.add(String(i.id)));
    pairs.push({
      movements: hareketler,
      islemler: grupIslemler,
      movementIds: hareketler.map((m) => String(m.id)),
      islemIds: grupIslemler.map((i) => String(i.id)),
      total: hareketToplam
    });
  });

  return pairs;
}

/**
 * Otomatik çoklu işlem eşleştirmelerini bulur (Firestore yazmaz).
 */
export function findAutoMultiMatchPairs(movements, islemler, existingMatches = [], usedBankIds = new Set(), usedIslemIds = new Set()) {
  const { lockedBankIds, lockedIslemIds } = getLockedIds(existingMatches);
  const usedB = new Set([...lockedBankIds, ...usedBankIds]);
  const usedI = new Set([...lockedIslemIds, ...usedIslemIds]);

  const freeMovements = filterIncomingBankMovements(movements).filter(
    (m) => m?.id && !usedB.has(String(m.id))
  );
  const freeIslemler = filterInvoiceEligibleIslemler(islemler).filter(
    (i) => i?.id && !usedI.has(String(i.id))
  );

  const results = [];

  freeMovements.forEach((movement) => {
    const availableIslemler = freeIslemler.filter((i) => !usedI.has(String(i.id)));
    const multi = suggestMultiMatchForMovement(movement, availableIslemler);
    if (!multi?.islemler?.length || multi.islemler.length < 2) return;

    const islemIds = multi.islemIds.map(String);
    if (islemIds.some((id) => usedI.has(id))) return;
    if (findDuplicateInvoiceMatch(existingMatches, movement.id, islemIds)) return;

    results.push({
      movement,
      islemler: multi.islemler,
      total: multi.total,
      islemIds
    });

    usedB.add(String(movement.id));
    islemIds.forEach((id) => usedI.add(id));
  });

  return results;
}

/**
 * Tek işlem ↔ çok banka hareketi adaylarını bulur (Firestore yazmaz).
 * En son çalışır: tek ve çoklu-işlem turları bir şey bulamamış hareketler üzerinde.
 */
export function findAutoMultiMovementPairs(
  movements,
  islemler,
  existingMatches = [],
  usedBankIds = new Set(),
  usedIslemIds = new Set()
) {
  const { lockedBankIds, lockedIslemIds } = getLockedIds(existingMatches);
  const usedB = new Set([...lockedBankIds, ...usedBankIds]);
  const usedI = new Set([...lockedIslemIds, ...usedIslemIds]);

  const freeMovements = filterIncomingBankMovements(movements).filter(
    (m) => m?.id && !usedB.has(String(m.id))
  );
  const freeIslemler = filterInvoiceEligibleIslemler(islemler).filter(
    (i) => i?.id && !usedI.has(String(i.id))
  );

  const results = [];

  freeIslemler.forEach((islem) => {
    if (usedI.has(String(islem.id))) return;
    const available = freeMovements.filter((m) => !usedB.has(String(m.id)));
    const combo = suggestMultiMovementMatchForIslem(islem, available);
    if (!combo?.movements?.length || combo.movements.length < 2) return;
    if (combo.movementIds.some((id) => usedB.has(id))) return;
    if (findDuplicateInvoiceMatch(existingMatches, combo.movementIds, islem.id)) return;

    results.push({ islem, movements: combo.movements, movementIds: combo.movementIds, total: combo.total });
    usedI.add(String(islem.id));
    combo.movementIds.forEach((id) => usedB.add(id));
  });

  return results;
}

/**
 * Bulunan otomatik eşleşmeleri Firestore'a yazar (özel matrah taslağı ile).
 */
/**
 * Kredi kartı faturası açılacak işlemler.
 * Daha önce (iptal edilmiş olsa bile) kart taslağı açılmış işlem tekrar aday olmaz —
 * personelin iptal ettiği taslak her turda geri gelmesin.
 */
/**
 * Fatura kesilecek kart satışı GRUPLARI.
 *
 * Eşik, çok parçalı işlemde grubun kart TOPLAMINA uygulanır — parça başına
 * değil. Grubun herhangi bir parçası için zaten taslak varsa grup atlanır.
 */
export const findKrediKartiCandidateGroups = (islemler = [], existingMatches = [], ruleRaw = null) => {
  const rule = mergeKrediKartiRule(ruleRaw);
  if (!rule.enabled) return [];
  const yapilmis = new Set();
  for (const m of existingMatches || []) {
    if (!isKrediKartiMatch(m)) continue;
    const ids = [m.krediKartiIslemId, m.islemData?.id, ...(m.islemIds || [])].filter(Boolean);
    ids.forEach((id) => yapilmis.add(String(id)));
  }
  return groupKrediKartiIslemler(islemler)
    .filter((grup) => !grup.islemler.some((x) => yapilmis.has(String(x.id))))
    .filter((grup) => evaluateKrediKartiGroup(grup, rule).ok);
};

export const findKrediKartiCandidates = (islemler = [], existingMatches = [], ruleRaw = null) => {
  const rule = mergeKrediKartiRule(ruleRaw);
  if (!rule.enabled) return [];
  const done = new Set(
    (existingMatches || [])
      .filter(isKrediKartiMatch)
      .map((m) => String(m.krediKartiIslemId || m.islemData?.id || ''))
      .filter(Boolean)
  );
  const seen = new Set();
  return (islemler || []).filter((islem) => {
    const id = String(islem?.id || '');
    if (!id || done.has(id) || seen.has(id)) return false;
    if (!evaluateKrediKartiCandidate(islem, rule).ok) return false;
    seen.add(id);
    return true;
  });
};

/**
 * Kuyruktan elle çıkarılan ("gizlenen") banka hareketlerini otomasyon dışında bırakır.
 *
 * Gizleme önceden yalnızca "Faturalandırılacak" listesini süzüyordu; otomatik
 * eşleştirme aynı hareketten yeni taslak ürettiği için hareket ertesi açılışta
 * onay kuyruğunda tekrar beliriyordu. Üç eşleştirme turu da bu listeyi kullanır.
 */
export function gizliOlmayanHareketler(movements, settings) {
  const gizli = new Set(
    (Array.isArray(settings?.hiddenBankMovementIds) ? settings.hiddenBankMovementIds : []).map(String)
  );
  if (!gizli.size) return movements || [];
  return (movements || []).filter((m) => !gizli.has(String(m?.id)));
}

export async function runAutoInvoiceMatches({
  sube,
  bankingId,
  movements,
  islemler,
  existingMatches,
  createdByPersonel,
  invoiceSettings = null
} = {}) {
  if (!sube) {
    return { created: 0, singleCreated: 0, multiCreated: 0, skipped: 0, errors: [], pairs: [], multiPairs: [] };
  }

  const personel = createdByPersonel || {
    id: 'system-auto-match',
    isim: 'Sistem',
    soyisim: 'Otomatik'
  };
  const resolvedBankingId = bankingId || getBankingIdForAppSube(sube);

  // Eşleşme listesi ve ayarlar tur başına bir kez okunur.
  // Önceden her createInvoiceMatch çağrısı koleksiyonun tamamını yeniden tarıyordu.
  const knownMatches = Array.isArray(existingMatches)
    ? [...existingMatches]
    : await getExistingInvoiceMatches(sube);
  const settings = invoiceSettings || (await loadInvoiceSettingsOnce(sube));

  const acikHareketler = gizliOlmayanHareketler(movements, settings);

  const pairs = findAutoMatchPairs(acikHareketler, islemler, knownMatches);
  let singleCreated = 0;
  let multiCreated = 0;
  let skipped = 0;
  const errors = [];
  const createdIds = [];
  const usedBank = new Set();
  const usedIslem = new Set();

  for (const pair of pairs) {
    try {
      const result = await createInvoiceMatch({
        sube,
        bankingId: resolvedBankingId,
        bankMovement: pair.movement,
        islem: pair.islem,
        matchScore: pair.score,
        matchStatus: INVOICE_PERSISTED_MATCH_STATUS.MATCHED,
        createdByPersonel: personel,
        autoMatched: true,
        draftSource: 'system-auto-match',
        existingMatches: knownMatches,
        invoiceSettings: settings
      });
      singleCreated += 1;
      createdIds.push(result.id);
      knownMatches.push(result);
      usedBank.add(String(pair.movement.id));
      usedIslem.add(String(pair.islem.id));
    } catch (err) {
      if (err?.code === 'DUPLICATE_MATCH') {
        skipped += 1;
      } else {
        errors.push({
          bankMovementId: pair.movement?.id,
          islemId: pair.islem?.id,
          message: err?.message || 'Otomatik eşleştirme hatası'
        });
      }
    }
  }

  const multiPairs = findAutoMultiMatchPairs(acikHareketler, islemler, knownMatches, usedBank, usedIslem);

  for (const pair of multiPairs) {
    try {
      const result = await createMultiInvoiceMatch({
        sube,
        bankingId: resolvedBankingId,
        bankMovement: pair.movement,
        islemler: pair.islemler,
        createdByPersonel: personel,
        autoMatched: true,
        draftSource: 'system-auto-multi-match',
        manualMatchReason: 'system-auto-multi-match',
        existingMatches: knownMatches,
        invoiceSettings: settings
      });
      multiCreated += 1;
      createdIds.push(result.id);
      knownMatches.push(result);
    } catch (err) {
      if (err?.code === 'DUPLICATE_MATCH') {
        skipped += 1;
      } else {
        errors.push({
          bankMovementId: pair.movement?.id,
          islemIds: pair.islemIds,
          message: err?.message || 'Otomatik çoklu eşleştirme hatası'
        });
      }
    }
  }

  // 3. tur: tek işlem ↔ çok banka hareketi (müşteri bedeli parça parça göndermiş)
  const multiMovementPairs = findAutoMultiMovementPairs(
    acikHareketler,
    islemler,
    knownMatches,
    usedBank,
    usedIslem
  );
  let multiMovementCreated = 0;

  for (const pair of multiMovementPairs) {
    try {
      const result = await createMultiMovementInvoiceMatch({
        sube,
        bankingId: resolvedBankingId,
        bankMovements: pair.movements,
        islem: pair.islem,
        createdByPersonel: personel,
        autoMatched: true,
        draftSource: 'system-auto-multi-movement',
        manualMatchReason: 'system-auto-multi-movement',
        existingMatches: knownMatches,
        invoiceSettings: settings
      });
      multiMovementCreated += 1;
      createdIds.push(result.id);
      knownMatches.push(result);
    } catch (err) {
      if (err?.code === 'DUPLICATE_MATCH') {
        skipped += 1;
      } else {
        errors.push({
          bankMovementIds: pair.movementIds,
          islemId: pair.islem?.id,
          message: err?.message || 'Çok hareketli otomatik eşleştirme hatası'
        });
      }
    }
  }

  // 3.5. tur: GRUP — çok hareket ↔ çok işlem. Tek tek hiçbiri tutmadığında,
  // aynı müşteri + aynı gün + toplamlar birebir eşitse tek taslak kurulur.
  const grupPairs = findAutoGroupPairs(acikHareketler, islemler, knownMatches, usedBank, usedIslem);
  let grupCreated = 0;

  for (const pair of grupPairs) {
    try {
      const result = await createGroupInvoiceMatch({
        sube,
        bankingId: resolvedBankingId,
        bankMovements: pair.movements,
        islemler: pair.islemler,
        createdByPersonel: personel,
        autoMatched: true,
        draftSource: 'system-auto-group-match',
        manualMatchReason: 'system-auto-group-match',
        existingMatches: knownMatches,
        invoiceSettings: settings
      });
      grupCreated += 1;
      createdIds.push(result.id);
      knownMatches.push(result);
      pair.movementIds.forEach((id) => usedBank.add(id));
      pair.islemIds.forEach((id) => usedIslem.add(id));
    } catch (err) {
      if (err?.code === 'DUPLICATE_MATCH') {
        skipped += 1;
      } else {
        errors.push({
          bankMovementIds: pair.movementIds,
          islemIds: pair.islemIds,
          message: err?.message || 'Grup eşleştirme hatası'
        });
      }
    }
  }

  // 4. tur: kredi kartı satışları (banka hareketi yok, işlemden taslak)
  let krediKartiCreated = 0;
  const krediKartiRule = mergeKrediKartiRule(settings?.krediKartiRule);
  if (krediKartiRule.enabled) {
    let kkIslemler = [];
    try {
      kkIslemler = await fetchKrediKartiIslemler(sube);
    } catch (err) {
      errors.push({ source: 'krediKarti', message: err?.message || 'Kredi kartı satışları okunamadı' });
    }
    const kkGruplar = findKrediKartiCandidateGroups(kkIslemler, knownMatches, krediKartiRule);
    for (const grup of kkGruplar) {
      const islem = grup.islemler[0];
      try {
        const result = await createKrediKartiInvoiceDraft({
          sube,
          islem,
          islemler: grup.islemler,
          createdByPersonel: personel,
          invoiceSettings: settings
        });
        krediKartiCreated += 1;
        createdIds.push(result.id);
        knownMatches.push(result);
      } catch (err) {
        if (err?.code === 'DUPLICATE_MATCH') {
          skipped += 1;
        } else {
          errors.push({
            islemId: islem?.id,
            source: 'krediKarti',
            message: err?.message || 'Kredi kartı taslağı oluşturulamadı'
          });
        }
      }
    }
  }

  return {
    created: singleCreated + multiCreated + multiMovementCreated + grupCreated + krediKartiCreated,
    singleCreated,
    multiCreated,
    multiMovementCreated,
    grupCreated,
    krediKartiCreated,
    skipped,
    errors,
    pairs,
    multiPairs,
    multiMovementPairs,
    grupPairs,
    createdIds
  };
}
